import { TextField, TextFieldProps } from '@mui/material'
import { observer } from 'mobx-react'
import React, { ChangeEvent } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form' //@ts-ignore
import ReactInputMask from 'react-input-mask'

type FormInputProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder?: string
  handleChangeCallback?: (value: FieldValues) => FieldValues
  handleEditValue?: (value: FieldValues) => T
  fullWidth: boolean
  rows?: number
  multiline?: boolean
  defaultError?: boolean
  mask?: string
  capitalizeFirstLetter?: boolean
  endAdornment?: JSX.Element
  onlyNumbers?: boolean
  onlyNumbersAndDots?: boolean
  onlyNumbersAndLetters?: boolean
}

const FormField = <T extends FieldValues>(
  props: FormInputProps<T> & TextFieldProps,
) => {
  const {
    control,
    fullWidth,
    label,
    name,
    defaultError,
    mask,
    capitalizeFirstLetter,
    endAdornment,
    onlyNumbers,
    onlyNumbersAndDots,
    onlyNumbersAndLetters,
    ...otherProps
  } = props

  const handleOnChange = (
    e: ChangeEvent<HTMLInputElement>,
    onChange: (...event: any[]) => void,
  ) => {
    if (capitalizeFirstLetter) {
      onCapitalizeChange(e, onChange)
    } else if (onlyNumbers) {
      onOnlyNumbersChange(e, onChange)
    } else if (onlyNumbersAndDots) {
      onOnlyNumbersAndDotsChange(e, onChange)
    } else if (onlyNumbersAndLetters) {
      onOnlyLettersAndNumbersChange(e, onChange)
    } else {
      onChange(e?.target?.value)
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error, isTouched, isDirty },
      }) => {
        const startError = !isTouched && !isDirty && defaultError

        if (mask) {
          return (
            <ReactInputMask
              mask={mask}
              onChange={onChange}
              maskPlaceholder={'/'}
              maskChar={null}
              value={value}
              formatChars={{
                9: '[0-9]',
                a: '[A-Za-z]',
                '*': '[A-Za-z0-9]',
                x: '[0-9]',
              }}
              {...field}
            >
              {() => (
                <TextField
                  value={value}
                  fullWidth
                  error={!!error?.message || startError}
                  label={label}
                  InputProps={{
                    inputProps: mask
                      ? { mask, ...otherProps?.inputProps }
                      : { ...otherProps?.inputProps },
                    endAdornment: endAdornment,
                  }}
                  {...field}
                  {...otherProps}
                />
              )}
            </ReactInputMask>
          )
        }

        return (
          <TextField
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleOnChange(e, onChange)
            }
            fullWidth
            error={!!error?.message || startError}
            label={label}
            InputProps={{
              inputProps: mask
                ? { mask, ...otherProps?.inputProps }
                : { ...otherProps?.inputProps },
              endAdornment: endAdornment,
            }}
            {...field}
            {...otherProps}
          />
        )
      }}
    />
  )
}

const onCapitalizeChange = (
  e: ChangeEvent<HTMLInputElement>,
  onChange: (value: string) => void,
) => {
  const input = e.target
  const originalValue = input.value
  const hasNumbers = /\d/.test(originalValue)

  if (hasNumbers) {
    // Если есть цифры - восстанавливаем предыдущее значение
    input.value = input.defaultValue
    return // Прерываем выполнение
  }

  // Капитализируем первую букву, если значение не пустое
  if (originalValue.length > 0) {
    const newValue =
      originalValue.charAt(0).toUpperCase() + originalValue.slice(1)
    input.value = newValue
    onChange(newValue)
  } else {
    onChange(originalValue)
  }
}

const onOnlyNumbersChange = (
  e: ChangeEvent<HTMLInputElement>,
  onChange: (...event: any[]) => void,
) => {
  e.target.value = e?.target?.value.replace(/\D/g, '')

  onChange(e?.target?.value)
}

const onOnlyLettersAndNumbersChange = (
  e: ChangeEvent<HTMLInputElement>,
  onChange: (...event: any[]) => void,
) => {
  e.target.value = e?.target?.value.replace(/[^a-zA-Zа-яА-Я0-9]/g, '')

  onChange(e?.target?.value)
}

const onOnlyNumbersAndDotsChange = (
  e: ChangeEvent<HTMLInputElement>,
  onChange: (...event: any[]) => void,
) => {
  e.target.value = e?.target?.value.replace(/[^\d.]/g, '')

  onChange(e?.target?.value)
}

export default observer(FormField)
